/**
 * Mobile Navigation Component
 * Handles mobile menu toggle and responsive navigation
 */

import { querySelector, addEventListener } from '../utils/dom.js';

export class MobileNav {
  constructor(toggleSelector = '.nav__toggle', navSelector = '.nav') {
    this.toggle = querySelector(toggleSelector);
    this.nav = querySelector(navSelector);

    if (!this.toggle) {
      console.warn('MobileNav: Toggle button not found');
      return;
    }

    if (!this.nav) {
      console.warn('MobileNav: Navigation element not found');
      return;
    }

    // State
    this.isOpen = false;

    // Bind methods
    this.handleToggle = this.handleToggle.bind(this);
    this.handleClickOutside = this.handleClickOutside.bind(this);
    this.handleEscape = this.handleEscape.bind(this);

    this.init();
  }

  init() {
    // Create mobile menu if it doesn't exist
    this.createMobileMenu();

    // Set up event listeners
    addEventListener(this.toggle, 'click', this.handleToggle);
    addEventListener(document, 'keydown', this.handleEscape);

    // Add click listeners to all links in mobile menu to auto-close
    this.addLinkListeners();
  }

  addLinkListeners() {
    if (!this.mobileMenu) return;

    // Get all links in the mobile menu
    const links = this.mobileMenu.querySelectorAll('a');

    links.forEach(link => {
      addEventListener(link, 'click', () => {
        // Close menu when any link is clicked
        this.close();
      });
    });
  }

  createMobileMenu() {
    // Check if mobile menu already exists
    let mobileMenu = querySelector('.nav__mobile-menu', this.nav);

    if (!mobileMenu) {
      // Create mobile menu container
      mobileMenu = document.createElement('div');
      mobileMenu.className = 'nav__mobile-menu';
      mobileMenu.setAttribute('aria-hidden', 'true');

      // Clone navigation links
      const navLinks = querySelector('.nav__links', this.nav);
      if (navLinks) {
        const mobileLinks = navLinks.cloneNode(true);
        mobileMenu.appendChild(mobileLinks);
      }

      // Clone navigation actions (Login/Try buttons)
      const navActions = querySelector('.nav__actions', this.nav);
      if (navActions) {
        const mobileActions = navActions.cloneNode(true);
        mobileMenu.appendChild(mobileActions);
      }

      // Insert into nav
      this.nav.appendChild(mobileMenu);
    }

    this.mobileMenu = mobileMenu;
  }

  handleToggle(e) {
    e.stopPropagation();
    this.isOpen ? this.close() : this.open();
  }

  open() {
    if (!this.mobileMenu) return;

    this.isOpen = true;
    this.mobileMenu.classList.add('is-open');
    this.mobileMenu.setAttribute('aria-hidden', 'false');
    this.toggle.setAttribute('aria-expanded', 'true');

    // Add click outside listener
    setTimeout(() => {
      addEventListener(document, 'click', this.handleClickOutside);
    }, 0);

    // Prevent body scroll
    document.body.style.overflow = 'hidden';
  }

  close() {
    if (!this.mobileMenu) return;

    this.isOpen = false;
    this.mobileMenu.classList.remove('is-open');
    this.mobileMenu.setAttribute('aria-hidden', 'true');
    this.toggle.setAttribute('aria-expanded', 'false');

    // Remove click outside listener
    document.removeEventListener('click', this.handleClickOutside);

    // Restore body scroll
    document.body.style.overflow = '';
  }

  handleClickOutside(e) {
    if (!this.mobileMenu.contains(e.target) && !this.toggle.contains(e.target)) {
      this.close();
    }
  }

  handleEscape(e) {
    if (e.key === 'Escape' && this.isOpen) {
      this.close();
    }
  }

  destroy() {
    document.removeEventListener('click', this.handleClickOutside);
    document.removeEventListener('keydown', this.handleEscape);

    if (this.mobileMenu) {
      this.mobileMenu.remove();
    }

    document.body.style.overflow = '';
  }
}
